import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Clock, Dumbbell, Flame, RefreshCw, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type DayName = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

type WorkoutType = 'Full Body' | 'Upper Body' | 'Lower Body' | 'Sport Specific' | 'Cardio';

type Exercise = {
  name: string;
  sets: number;
  reps: string;
};

type DayRoutine = {
  day: DayName;
  focus: string;
  type: WorkoutType | string;
  exercises: Exercise[];
};

type Routine = {
  daysPerWeek: number;
  level: string;
  category: string;
  routine: DayRoutine[];
};

type RoutineDisplayProps = {
  routine: Routine;
  onRegenerate: () => void;
  onSave: () => void;
  onBack: () => void;
};

const dayColors: Record<DayName, string> = {
  Monday: 'from-violet-500 to-purple-600',
  Tuesday: 'from-blue-500 to-cyan-600',
  Wednesday: 'from-emerald-500 to-teal-600',
  Thursday: 'from-orange-500 to-amber-600',
  Friday: 'from-rose-500 to-pink-600',
  Saturday: 'from-indigo-500 to-blue-600',
  Sunday: 'from-fuchsia-500 to-purple-600',
};

const typeIcons: Record<WorkoutType, string> = {
  'Full Body': '💪',
  'Upper Body': '🏋️',
  'Lower Body': '🦵',
  'Sport Specific': '🎯',
  Cardio: '❤️‍🔥',
};

export default function RoutineDisplay({
  routine,
  onRegenerate,
  onSave,
  onBack,
}: RoutineDisplayProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Your Training Plan</h2>
          <p className="text-slate-500 mt-1">
            {routine.daysPerWeek} days/week • {routine.level} level • {routine.category} focus
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Edit
          </Button>
          <Button
            variant="outline"
            onClick={onRegenerate}
            className="gap-2 hover:bg-violet-50 hover:text-violet-600 hover:border-violet-300"
          >
            <RefreshCw className="w-4 h-4" />
            Regenerate
          </Button>
          <Button
            onClick={onSave}
            className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white border-0"
          >
            <Save className="w-4 h-4" />
            Save
          </Button>
        </div>
      </motion.div>

      {/* Routine Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence>
          {routine.routine.map((day, index) => (
            <motion.div
              key={day.day}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                delay: index * 0.1,
                type: 'spring',
                stiffness: 100,
              }}
            >
              <Card className="overflow-hidden h-full hover:shadow-xl transition-shadow duration-300">
                {/* Day Header */}
                <div className={cn('bg-gradient-to-r p-4 text-white', dayColors[day.day])}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-lg">{day.day}</h3>
                      <p className="text-white/80 text-sm">{day.focus}</p>
                    </div>
                    <span className="text-3xl">{typeIcons[day.type as WorkoutType] || '🏃'}</span>
                  </div>
                  <Badge variant="secondary" className="mt-2 bg-white/20 text-white border-0">
                    {day.type}
                  </Badge>
                </div>

                {/* Exercises */}
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {day.exercises.map((exercise, exIndex) => (
                      <motion.div
                        key={exIndex}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + exIndex * 0.05 }}
                        className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                            <Dumbbell className="w-4 h-4 text-slate-600" />
                          </div>
                          <span className="font-medium text-slate-700 text-sm">
                            {exercise.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="outline" className="bg-white">
                            {exercise.sets}×{exercise.reps}
                          </Badge>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-slate-500">
                    <div className="flex items-center gap-1">
                      <Flame className="w-4 h-4 text-orange-400" />
                      <span>{day.exercises.length} exercises</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <span>~{Math.round(day.exercises.length * 8)} min</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
